<template>
  <component
    :is="state$$.layout"
    v-bind="layoutProps"
    :api-version="modelRef.apiVersion"
    :class="printing ? 'no-overflow' : ''"
  >
    <template #header__left>
      <div class="header">
        <div class="header__left">
          <div
            class="header__logo header__logo-fomento "
            @click="openMTMS"
          >
            <img
              class="header__img-logo"
              src="@/assets/images/logoFomento.svg"
              alt="Logo MITMA"
            />
          </div>
          <div
            v-if="!isMobileVersionRef"
            class="header__icons-container header__menu-left"
          >
            <MapMenu />
          </div>
        </div>
        <div class="header__right">
          <!-- TEXTO NIPO, a la izquierda del logo Fomento -->
          <!-- <div class="header__nipo">
            NIPO: 196-24-065-5
          </div> -->
          <div class="header__logo header__logo-moc">
            <router-link :to="{ name: 'Home'}">
              <img
                class="header__img-logo"
                src="@/assets/images/Logo-mapa-carreteras-2026.png"
                alt="Logo Carreteras"
              />
            </router-link>
          </div>
          <div
            v-if="isMobileVersionRef"
            class="header__icons-container header__menu-right"
          >
            <MapMenu
              :only-bars="true"
              @toggle-sidebar="toggleSidebar"
              @click-item="closeSidebar"
            />
          </div>
        </div>
      </div>
    </template>
    <template #header__right>
      <Toast />
    </template>
    <template #footer>
      <ReportList />
    </template>
    <template #sidebar>
      <div class="no-icon">
        <CollapsePanel
          ref="sidebarPanel"
          position="right"
          class="collapse-panel__toggler--hidden"
        >
          <MapMenu
            sidebar
            @click-item="closeSidebar"
          />
        </CollapsePanel>
      </div>
    </template>
  </component>
</template>

<script setup>

import {ref,
  nextTick,
  onMounted,
  provide,
  reactive,
  computed,
  watch,
} from 'vue';
import {useStore} from 'vuex';
import Toast from 'primevue/toast';
import {
  // TheFooter,
  // TheUserMenu,
  useImmer,
  // useVuexModule,
  CollapsePanel,
} from '@sttedra_ui/framework';
import MapMenu from '@/components/map-menu/MapMenu';
import ReportList from '@/components/report-list/ReportList';

import AppViewModel from './app.view-model';
import AppViewState from './app.view-state';
import {getConfigurations} from '@/api/visor/configuration/configuration.api';
import {useNProgress} from '@vueuse/integrations/useNProgress';
import useHandleMOCError from '@/composables/use-handle-moc-error';
import {errorToastOptions} from '@/util-toast';

// #region Props, Emits
// #endregion

// #region Params, Ref, Reactives, Computed
const sidebarPanel = ref();
const store = useStore();
const {handleMOCError} = useHandleMOCError();
const state = new AppViewState();
const state$$ = reactive(state);

const triggerSidebar = computed(() => store.state.tutorial.triggerSidebar);
const printing = computed(() => store.state.app.printing);
const fontSizeVariation = computed(() => store.state.app.fontSizeVariation);
const isMobileVersionRef = ref(false);

// const isMobileVersionComputed = computed(() => window.matchMedia('(max-width: 769px)'));

// Priority order of map type:
// 1- Cookie param
// 2- set to 'raster' map type
const layoutProps = computed(() => {
  return {
    mapConfig: config.value,
    mapType: 'vector',
  };
});
const model = new AppViewModel();
const config = ref();
const {ref: modelRef /* , update: updateModel */} = useImmer(model);
// #endregion

// #region Functions
function toggleSidebar() {
  sidebarPanel.value.toggle();
}
function openSidebar() {
  sidebarPanel.value.toggle(true);
}
function closeSidebar() {
  sidebarPanel.value.toggle(false);
}
function setIconUrls(urlS3) {
  const BASE_URL = urlS3 + '/LegendGraphic/ICON_NAME.png';
  const urlWrap = (url) => 'url(\'' + url + '\')';
  // POIs icons
  const CATEGORY_SPA_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Balneario');
  const CATEGORY_CASTLE_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Castillo');
  const CATEGORY_COTTAGE_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'CasaRural');
  const CATEGORY_CAVE_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Cueva');
  const CATEGORY_HERMITAGE_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Ermita');
  const CATEGORY_GARDEN_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Jardin');
  const CATEGORY_MONASTERY_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Monasterio');
  const CATEGORY_MONUMENT_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Monumento');
  const CATEGORY_RUIN_ICON_URL = BASE_URL.replaceAll('ICON_NAME', 'Ruina');
  document.documentElement.style.setProperty('--url-icon-category-spa',
    urlWrap(CATEGORY_SPA_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-castle',
    urlWrap(CATEGORY_CASTLE_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-cottage',
    urlWrap(CATEGORY_COTTAGE_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-cave',
    urlWrap(CATEGORY_CAVE_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-hermitage',
    urlWrap(CATEGORY_HERMITAGE_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-garden',
    urlWrap(CATEGORY_GARDEN_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-monastery',
    urlWrap(CATEGORY_MONASTERY_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-monument',
    urlWrap(CATEGORY_MONUMENT_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-ruin',
    urlWrap(CATEGORY_RUIN_ICON_URL));
  // Mountain ports icons
  const CATEGORY_SNOW_PROBLEMS_PORTS_ICON_URL =
    BASE_URL.replaceAll('ICON_NAME', 'PuertosDeMontanaConProblemasDeNieve');
  const CATEGORY_SNOW_PROBLEMS_BIG_PORTS_ICON_URL =
      BASE_URL.replaceAll('ICON_NAME', 'GranPuertoDeMontana');
  const CATEGORY_NO_SNOW_PROBLEMS_PORTS_ICON_URL =
      BASE_URL.replaceAll('ICON_NAME', 'PuertosDeMontanaSinProblemasDeNieve');
  document.documentElement.style.setProperty('--url-icon-category-snow-problems-ports',
    urlWrap(CATEGORY_SNOW_PROBLEMS_PORTS_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-snow-problems-big-ports',
    urlWrap(CATEGORY_SNOW_PROBLEMS_BIG_PORTS_ICON_URL));
  document.documentElement.style.setProperty('--url-icon-category-no-snow-problems-ports',
    urlWrap(CATEGORY_NO_SNOW_PROBLEMS_PORTS_ICON_URL));
  // Public works icons
  const CATEGORY_AIRPORT_ICON_URL =
      BASE_URL.replaceAll('ICON_NAME', 'Aeropuerto');
  document.documentElement.style.setProperty('--url-icon-category-airport',
    urlWrap(CATEGORY_AIRPORT_ICON_URL));
  const CATEGORY_STATION_ICON_URL =
    BASE_URL.replaceAll('ICON_NAME', 'EstacionFerrocarril');
  document.documentElement.style.setProperty('--url-icon-category-station',
    urlWrap(CATEGORY_STATION_ICON_URL));
  const CATEGORY_LIGHTHOUSE_ICON_URL =
      BASE_URL.replaceAll('ICON_NAME', 'Faro');
  document.documentElement.style.setProperty('--url-icon-category-lighthouse',
    urlWrap(CATEGORY_LIGHTHOUSE_ICON_URL));
  const CATEGORY_DAM_ICON_URL =
    BASE_URL.replaceAll('ICON_NAME', 'Presa');
  document.documentElement.style.setProperty('--url-icon-category-dam',
    urlWrap(CATEGORY_DAM_ICON_URL));
  const CATEGORY_BRIDGE_ICON_URL =
    BASE_URL.replaceAll('ICON_NAME', 'Puente');
  document.documentElement.style.setProperty('--url-icon-category-bridge',
    urlWrap(CATEGORY_BRIDGE_ICON_URL));
  const CATEGORY_TUNNEL_ICON_URL =
    BASE_URL.replaceAll('ICON_NAME', 'Tunel');
  document.documentElement.style.setProperty('--url-icon-category-tunnel',
    urlWrap(CATEGORY_TUNNEL_ICON_URL));
}
function setFontSizeVariation(fontSizeVariationNumber) {
  if (fontSizeVariationNumber != null) {
    const fontSizeVariation = fontSizeVariationNumber + 'px';
    document.documentElement.style.setProperty('--font-size-variation',
      fontSizeVariation);
  }
}

function openMTMS() {
  window.open('https://www.mitma.gob.es/');
}


function handlerWidthBody() {
  isMobileVersionRef.value = document.body.offsetWidth <= 769;
}


async function init() {
  window.removeEventListener('resize', handlerWidthBody);
  window.addEventListener('resize', handlerWidthBody);
  handlerWidthBody();

  // #region Loading
  try {
    config.value = await getConfigurations();
    await nextTick();
    document.body.classList.add('app-mounted');
    setIconUrls(config.value.urlS3);
    setFontSizeVariation(fontSizeVariation.value);
    // Nprogress Configuration
    useNProgress(null, {
      minimum: 0.4,
      trickleSpeed: 200,
      parent: '.app',
    });
  } catch (err) {
    const toastOptions = errorToastOptions('Error al iniciar mapa');
    handleMOCError(err, toastOptions);
  }

  // #endregion
}
// #endregion


// #region Effects, Hooks
onMounted(init);
watch(fontSizeVariation, (newFontSizeVariationNumber) => {
  setFontSizeVariation(newFontSizeVariationNumber);
});
watch(triggerSidebar, (newTrigger) => {
  const open = newTrigger.openSidebar;
  if (!open) {
    closeSidebar();
  }
  if (!!open) {
    openSidebar();
  }
});
// #endregion


// #region Provide, Expose
provide('appState$$', state$$);
provide('CONFIG_MAP', config);
// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";
.no-overflow {
  overflow: hidden;
}
.header {
  height: $brand__boxspace--headerheight;
  display: flex;
  flex-direction: row;
  flex: 0 0 100%;
  background: linear-gradient(
    90deg,
    $brand__color--blue,
    rgba($brand__color--blue, 0.5) 40%,
    rgba($brand__color--black, 0)
  );
  z-index: 9;

  &__logo {
    display: flex;
  }
  &__logo-fomento, &__logo-fomento a {
    cursor: pointer;
    @include logo-fomento-width($brand__boxspace--headerheight);
  }
  &__menu-left {
    display: none;
    @include min-screen-width($brand__breakpoint--md) {
      display: flex;
      flex: 1;
    }
  }
  &__menu-right {
    display: flex;
    @include min-screen-width($brand__breakpoint--md) {
      display: none;
    }
  }
  &__icons-container {
    flex-direction: row;
    padding: 0.6rem;
    // flex: 1;
    display: flex;
  }
  &__logo-moc {
    height: 100%;
    // cursor: pointer;
    // @include logo-moc-width($brand__boxspace--headerheight);
  }
  &__logo-moc a {
    display: -webkit-inline-box; // añadido en fix safari
    // cursor: pointer;
    // @include logo-moc-width($brand__boxspace--headerheight);
  }

  &__img-logo {
     height: 100%; // añadido en fix safari
  }
  a:focus {
    outline: none;
  }
  &__left {
    /* fit-content does not work on firefox */
    display: flex;
    flex-direction: row;
    flex: 1 1 100%;
    height: 100%;
    width: 100%; // fix safari
    justify-content: flex-start;
    // @include max-screen-width($brand__breakpoint--md) {
    //   @include logo-fomento-width($brand__boxspace--headerheight);
    // }
  }

  &__right {
    /* fit-content does not work on firefox */
    display: flex;
    flex-direction: row;
    height: 100%;
    justify-content: flex-end;
    align-items: center;
    @include min-screen-width($brand__breakpoint--md) {
      justify-content: flex-end;
    }
  }

  // &__nipo {
  //   color: #fff;
  //   margin-right: 10px;
  //   font-size: 0.7rem;
  //   font-family: 'Arial', sans-serif;

  // }
}
.no-icon :deep(.p-button) {
  display: none;
}
.no-icon :deep(.collapse-panel) {
  padding: 0;
}
</style>
